import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useCallback, useEffect, useState } from 'react'
import { $createParagraphNode, $createTextNode, $getRoot, $getSelection, $isRangeSelection, TextNode } from 'lexical'
import { IconButton } from 'ComponentLibrary/src/atoms/Buttons/IconButton'
import { Check as CheckIcon, Paperclip, X as CancelIcon } from 'react-feather'
import { $convertToMarkdownString, TRANSFORMERS } from '@lexical/markdown'
import { $isRootTextContentEmpty } from '@lexical/text'
import { Button as MuiButton, Stack, styled, useTheme } from '@mui/material'
import { MessageIcon, WHITE_LISTED_FILES } from 'ComponentLibrary'
import { isEmpty } from 'lodash'
import { useBeautifulMentions } from 'lexical-beautiful-mentions'

interface EditorNode {
    getType: () => string
    getTextContent: () => string
    replace: (node: TextNode) => void
    getChildren?: () => EditorNode[]
}

type SubmitPluginProps = {
    onSubmit: (value: string, mentions: number[]) => void
    onCancel?: () => void
    submitVariant?: 'inline' | 'toolbar'
    setSelectedFiles?: (files: File[]) => void
    selectedFiles?: File[]
    disabled?: boolean
}

function traverseAndReplaceNode(node: EditorNode) {
    if (node.getType() === 'custom-beautifulMention') {
        const textContent = node.getTextContent()
        const textNode = $createTextNode(textContent)
        node.replace(textNode)
    } else if (node.getChildren) {
        node.getChildren().forEach((childNode) => {
            traverseAndReplaceNode(childNode)
        })
    }
}

export const SubmitPlugin = ({
    onSubmit,
    onCancel,
    submitVariant,
    selectedFiles,
    setSelectedFiles,
    disabled = false,
}: SubmitPluginProps) => {
    const theme = useTheme()
    const [editor] = useLexicalComposerContext()
    const [hasNoMessage, setHasNoMessage] = useState(true)

    const { getMentions } = useBeautifulMentions()
    const handleSubmit = useCallback(() => {
        let markdown = ''
        if (disabled) {
            return
        }

        const currentMentions: number[] = getMentions().map((mention) => mention?.data?.id as number) || []

        editor.update(
            () => {
                const root = $getRoot()
                const allNodes = root.getChildren()
                allNodes.forEach((node) => {
                    traverseAndReplaceNode(node)
                })
                markdown = $convertToMarkdownString(TRANSFORMERS)
                root.clear()

                const paragraph = $createParagraphNode()
                root.append(paragraph)
                paragraph.selectStart()

                const selection = $getSelection()
                if ($isRangeSelection(selection)) {
                    selection.format = 0
                    selection.setStyle('')
                }
            },
            { discrete: true }
        )
        onSubmit(markdown, currentMentions)
    }, [disabled, onSubmit, editor, getMentions])

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const isEditorEmpty = $isRootTextContentEmpty(false, true)
                setHasNoMessage(isEditorEmpty)
            })
        })
    }, [editor])

    if (submitVariant === 'inline') {
        return (
            <Stack direction="row" gap={theme.spacing(1)}>
                {setSelectedFiles && (
                    <StyledButton variant="contained" component="label" disableElevation startIcon={<Paperclip size={20} />}>
                        <input
                            hidden
                            multiple
                            id="contained-button-file"
                            type="file"
                            onChange={(e) => {
                                e.target.files && setSelectedFiles([...e.target.files])
                                e.target.value = '' // This resets the input and makes sure the onChange triggers again even if we upload the same file again
                            }}
                            accept={WHITE_LISTED_FILES}
                        />
                    </StyledButton>
                )}
                <StyledButton
                    variant="contained"
                    component="label"
                    startIcon={<MessageIcon />}
                    onClick={handleSubmit}
                    disabled={disabled || (hasNoMessage && isEmpty(selectedFiles))}
                    disableElevation
                />
            </Stack>
        )
    }
    return (
        <Stack direction="row" gap={theme.spacing(2)}>
            <IconButton icon={<CancelIcon />} onClick={onCancel} />
            <IconButton icon={<CheckIcon />} onClick={handleSubmit} disabled={disabled} />
        </Stack>
    )
}

const StyledButton = styled(MuiButton)(({ theme }) => ({
    color: theme.palette.customtext.darkblue100,
    backgroundColor: theme.palette.white,
    minWidth: 'unset',
    height: 'fit-content',
    padding: '10px',
    '& .MuiButton-startIcon': {
        margin: 0,
    },
    '&:hover': {
        backgroundColor: theme.palette.secondary1.grey,
    },
})) as typeof MuiButton
